import { Box } from 'src/design/atoms/Box';
import { Group } from 'src/design/atoms/Group';
import { Switch } from 'src/design/atoms/Switch';
import { Text } from 'src/design/atoms/Text';
import { TextInput } from 'src/design/atoms/TextInput';
import { DatesRangeValue } from '@mantine/dates';
import { IconSearch, IconThumbDownFilled } from '@tabler/icons-react';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useEffect, useMemo, useState } from 'react';
import { AdminConversationDto } from 'src/api/FiretrolApiClient';
import { useAdminConversations } from 'src/api/queries/chatAdmin/useAdminConversations';
import useDebounce from 'src/hooks/useDebounce';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { AnchorLink } from 'src/design/molecules/AnchorLink';
import { ConversationDetailModal } from 'src/routes/Settings/AIAssistant/ConversationDetailModal';
import { formatDateTime } from 'src/utils';

export interface IConversationsTableProps {
  /** Fixed filter — when set, the User column is hidden and every request is scoped to this user. */
  userId?: number;
  dateRange?: DatesRangeValue;
}

const getColumns = (
  showUserColumn: boolean,
): MRT_ColumnDef<AdminConversationDto>[] => {
  const userColumn: MRT_ColumnDef<AdminConversationDto> = {
    accessorKey: 'userName',
    header: 'User',
    Cell: ({ cell, row }) => (
      // Wrapper stops the click before it reaches the row's onClick (which
      // opens the conversation detail modal) so the link navigates instead.
      <Box
        onClick={(e) => e.stopPropagation()}
        style={{ display: 'inline-block' }}
      >
        <AnchorLink to={`/settings/ai-assistant/users/${row.original.userId}`}>
          {cell.getValue<string>()}
        </AnchorLink>
      </Box>
    ),
  };

  const rest: MRT_ColumnDef<AdminConversationDto>[] = [
    { accessorKey: 'title', header: 'Title' },
    { accessorKey: 'model', header: 'Model' },
    { accessorKey: 'page', header: 'Page' },
    {
      accessorKey: 'negativeFeedbackCount',
      header: 'Negative Feedback',
      Cell: ({ cell }) => {
        const count = cell.getValue<number>();
        return count > 0 ? (
          <Group gap={4} wrap="nowrap">
            <Box c="red.7" style={{ display: 'flex' }}>
              <IconThumbDownFilled size={14} aria-hidden />
            </Box>
            <Text size="sm" c="bright">
              {count}
            </Text>
          </Group>
        ) : null;
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      Cell: ({ cell }) => formatDateTime(cell.getValue<string>()),
    },
    {
      accessorKey: 'updatedAt',
      header: 'Updated',
      Cell: ({ cell }) => formatDateTime(cell.getValue<string>()),
    },
  ];

  return showUserColumn ? [userColumn, ...rest] : rest;
};

export const ConversationsTable: React.FC<IConversationsTableProps> = ({
  userId,
  dateRange,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [negativeFeedbackOnly, setNegativeFeedbackOnly] = useState(false);
  const [from, to] = dateRange ?? [undefined, undefined];

  const columns = useMemo(() => getColumns(!userId), [userId]);

  const tableState = useTableState({
    identifier: userId
      ? 'ai-assistant-user-conversations'
      : 'ai-assistant-conversations',
    columns,
    defaultSorting: [{ id: 'updatedAt', desc: true }],
  });
  const { pagination, setPagination } = tableState;

  const conversationsQuery = useAdminConversations({
    userId,
    from: from ?? undefined,
    to: to ?? undefined,
    search: debouncedSearch || undefined,
    hasNegativeFeedback: negativeFeedbackOnly || undefined,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
  });
  const conversations = conversationsQuery.data?.items ?? [];
  const totalCount = conversationsQuery.data?.totalCount ?? 0;

  // Jump back to page 1 whenever a filter changes so we never request a page
  // number beyond the newly-filtered result set.
  useEffect(() => {
    setPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
    );
  }, [userId, debouncedSearch, negativeFeedbackOnly, from, to]);

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: false,
      enableClickToCopy: false,
      columns,
      data: conversations,
      manualPagination: true,
      manualSorting: false,
      manualFiltering: true,
      // Per-column filtering isn't wired to the server; only the toolbar
      // search box is. Hide the (non-functional) column filter row.
      enableColumnFilters: false,
      mantineTableBodyRowProps: ({ row }) => ({
        onClick: () => setSelectedId(row.original.id),
        style: { cursor: 'pointer' },
      }),
      renderTopToolbarCustomActions: () => (
        <Group>
          <TextInput
            placeholder="Search conversations..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            w={260}
          />
          <Switch
            label="Has negative feedback"
            checked={negativeFeedbackOnly}
            onChange={(e) => setNegativeFeedbackOnly(e.currentTarget.checked)}
          />
        </Group>
      ),
    },
    getRowId: (row) => row.id,
    totalCount,
    isLoading: conversationsQuery.isLoading,
    isFetching: conversationsQuery.isFetching,
    isError: conversationsQuery.isError,
    onRefresh: conversationsQuery.refetch,
  });

  return (
    <>
      {table}
      <ConversationDetailModal
        conversationId={selectedId}
        onClose={() => setSelectedId(null)}
      />
    </>
  );
};
